import { useContext } from "react";
import { ReactReduxContext } from "react-redux";

import type { RootState } from "../store";
import { AppStrings } from "../utils/constants/AppStrings";

/**
 * Active client's currency symbol.
 *
 * Primary source is `app_currency_config.default_currency_symbol` from the
 * platform config endpoint (Redux). The operator panel loads this at boot.
 *
 * Fallback is `AppStrings.COUNTRY_CURRENCY_SYMBOL`, which the CDN strings.json
 * injects at runtime. This matters for the affiliate portal, which intentionally
 * skips the platform config fetch (`App.js` short-circuits on `/affiliate-portal`).
 *
 * We read through `ReactReduxContext` directly (instead of `useAppSelector`)
 * so the hook stays safe outside a Provider — many component-level tests render
 * without a Redux store and would otherwise crash. The currency symbol is set
 * once at boot and never changes during a session, so a one-shot `getState()`
 * read is equivalent to a subscription here.
 *
 * Returns `undefined` only when both sources are empty — consumers should fall back to "$".
 */
export function useCurrencySymbol(): string | undefined {
  const reduxContext = useContext(ReactReduxContext);
  const configSymbol = reduxContext
    ? (reduxContext.store.getState() as RootState).config?.appConfig
        ?.app_currency_config?.default_currency_symbol
    : undefined;
  if (configSymbol) return configSymbol;
  const stringsSymbol = AppStrings.COUNTRY_CURRENCY_SYMBOL;
  return stringsSymbol ? stringsSymbol : undefined;
}

export default useCurrencySymbol;
